// 11. 10. 2026 (owner, BACKLOG #33, docs/crew.md; plan and the numbers' reasons: docs/notes/crews.md): Crews. Up to five
// survivors under one flag (WARDEN's rule no. 3). The survivor groups (`clans`) became crews: same tables, new rules. Every
// crew has the same perks by its level (1–10, reset with the Sweep), earned with crew XP from four sources: the weekly
// crew contract, the horde, the members' runs (energy spent) and donations. A member gets each level's chest once a
// season (48 h in the crew and some XP given since the level before). Every number here is a proposal to be measured.

import { STACKS } from './content/resources';
import { seasonMaterials, seasonName } from './season';
import type { StackKind, StackMap } from './types';

export const CREW_RULES = {
  /** Off: the crew rules are not applied (the old survivor groups would need their research back; not planned). */
  on: true,
  maxMembers: 5,
  maxOfficers: 2,
  /** Joining: from level 8, or after the account's first week (whichever comes first). */
  joinLevel: 8,
  joinAfterDays: 7,
  /** Founding: level 10 and 1,000 Scrap. */
  foundLevel: 10,
  foundScrap: 1000,
  /** After leaving a crew, another one (join or found) only after this many hours. */
  rejoinHours: 24,
  /** A leader not seen for this many days hands the crew to the most active officer (or member). */
  leaderAwayDays: 14,
  /** A level's chest: only for a member in the crew this long who gave XP since the level before. */
  chestMinHours: 48,
  /** Applications kept for an invite-only crew. */
  applicationsMax: 20,
  maxLevel: 10,
  /** Level 6: more loot while another member is on a run in the same zone. */
  zoneLoot: 0.05,
  /** The lore line shown when a crew is full. */
  wardenRule: 'WARDEN rule no. 3: "Gatherings of more than five residents are prohibited."',
};

/**
 * Crew XP for each level (the season's total): index 0 is level 1. 13. 10. 2026 (owner, after `d8ce`): a crew of five
 * active bots made about 3,000 crew XP by day 14 and 9,500 by day 40 (about 230 a day, not the planned 490; all four
 * sources used: contract 34%, horde 17%, runs 38%, donations 12%), so the thresholds are cut to level 5 on about day 14
 * and level 10 on about day 40 (before: 750, 2,000, 4,000, 6,500, 9,000, 11,500, 14,000, 16,750, 19,500). 14. 10. 2026
 * (owner, after `d8ce2`): level 5 came on day 19 (a crew had about 1,850 by day 13; bots join about day 3), level 10 on day
 * 40, so the early levels are cheaper: level 5 at 2,050 for about day 14, level 10 stays (before: 350, 925, 1,850, 3,000,
 * 4,250, 5,500, 6,750, 8,125).
 */
export const CREW_LEVEL_XP = [0, 300, 700, 1300, 2050, 3600, 5000, 6500, 8000, 9500] as const;

export function crewLevelOf(xp: number): number {
  let lv = 1;
  for (let i = 1; i < CREW_LEVEL_XP.length; i++) if (xp >= CREW_LEVEL_XP[i]) lv = i + 1;
  return Math.min(CREW_RULES.maxLevel, lv);
}

/** The level and the way to the next one (`next` null at level 10). */
export function crewLevelProgress(xp: number): { level: number; from: number; next: number | null; into: number; need: number | null } {
  const level = crewLevelOf(xp);
  const from = CREW_LEVEL_XP[level - 1];
  const next = level < CREW_RULES.maxLevel ? CREW_LEVEL_XP[level] : null;
  return { level, from, next, into: xp - from, need: next == null ? null : next - from };
}

export type CrewPerkId = 'chat' | 'contract' | 'hordeBonus' | 'send' | 'revenge' | 'zoneLoot' | 'radio' | 'hordeBonus2' | 'flag' | 'title';

/** The perk each level opens (the same for every crew; they stay for the season). */
export const CREW_PERKS: { level: number; id: CrewPerkId; name: string; text: string }[] = [
  { level: 1, id: 'chat', name: 'Crew chat and flag', text: 'A chat for the crew, a name, a tag and a flag.' },
  { level: 2, id: 'contract', name: 'Weekly crew contract', text: 'Three goals for the whole crew every week, for crew XP.' },
  { level: 3, id: 'hordeBonus', name: 'Stand together', text: '+5% horde damage for every member who fights when at least two of you fight the same evening.' },
  { level: 4, id: 'send', name: 'Supply line', text: 'Send resources to a crewmate, up to a daily limit that grows with the crew level.' },
  { level: 5, id: 'revenge', name: 'Revenge for a mate', text: "Beat whoever holds a crewmate's stolen item and it goes back to them." },
  { level: 6, id: 'zoneLoot', name: 'Not alone out there', text: '+5% loot while another member is on a run in the same zone.' },
  { level: 7, id: 'radio', name: 'On the air', text: 'Danny Dawn mentions the crew on the radio.' },
  { level: 8, id: 'hordeBonus2', name: 'Hold the line', text: '+10% horde damage instead of +5%, and a bigger shared horde chest.' },
  { level: 9, id: 'flag', name: 'A better flag', text: 'New shapes and symbols for the flag.' },
  { level: 10, id: 'title', name: 'The Last Five', text: 'The title The Last Five for every member.' },
];

export const crewHasPerk = (level: number, id: CrewPerkId) => CREW_PERKS.some((p) => p.id === id && p.level <= level);

// ---- The level chests ----------------------------------------------------------------------------------------------

export interface CrewChestDef {
  stacks?: StackMap;
  /** Materials for the member's level, as the horde gives them (`seasonMaterials`). */
  materialsDays?: number;
  item?: 'rare' | 'epic';
  blueprint?: 'rare';
  shards?: number;
  title?: string;
  /** Level 10: the crew outfit (an icon in the Collection for now). */
  outfit?: boolean;
}

/** What a level's chest holds for each member (once a season; level 1 has none). */
export const CREW_CHESTS: Record<number, CrewChestDef> = {
  2: { stacks: { scrap: 60, bandage: 2 } },
  3: { materialsDays: 1 },
  4: { stacks: { medkit: 1 }, materialsDays: 1 },
  5: { item: 'rare', shards: 2 },
  6: { materialsDays: 1, stacks: { ration: 2, hot_stew: 1 } },
  7: { stacks: { repair_kit: 2 }, materialsDays: 1 },
  8: { blueprint: 'rare', shards: 3 },
  9: { materialsDays: 1.5, shards: 3 },
  10: { item: 'epic', shards: 5, title: 'The Last Five', outfit: true },
};

/** The chest's stacks for a member of this level (materials, the fixed stacks and the Relic Shards; no item). */
export function crewChestStacks(crewLevel: number, memberLevel: number): StackMap {
  const d = CREW_CHESTS[crewLevel];
  if (!d) return {};
  const out: StackMap = { ...(d.stacks ?? {}) };
  if (d.materialsDays) for (const [k, v] of Object.entries(seasonMaterials(d.materialsDays, memberLevel)) as [StackKind, number][]) out[k] = (out[k] ?? 0) + v;
  if (d.shards) out.relic_shard = (out.relic_shard ?? 0) + d.shards;
  return out;
}

/** The chest in words, for the level list ("Rare item for your level · 2 Relic Shards"). */
export function crewChestText(crewLevel: number): string {
  const d = CREW_CHESTS[crewLevel];
  if (!d) return '';
  const parts: string[] = [];
  for (const [k, v] of Object.entries(d.stacks ?? {}) as [StackKind, number][]) parts.push(`${v} ${v === 1 ? STACKS[k].name : (STACKS[k].plural ?? STACKS[k].name)}`);
  if (d.materialsDays) parts.push('materials for your level');
  if (d.item) parts.push(`${d.item === 'rare' ? 'Rare' : 'Epic'} item for your level`);
  if (d.blueprint) parts.push('Rare Blueprint');
  if (d.shards) parts.push(`${d.shards} Relic Shards`);
  if (d.title) parts.push(`title ${d.title}`);
  if (d.outfit) parts.push('the crew outfit');
  return parts.join(' · ');
}

/** A member gets the chest of a level reached at `at`: 48 h in the crew and some XP given since the level before. */
export function crewChestEligible(m: { joinedAt: number; levelXp: number }, at: number): boolean {
  return at - m.joinedAt >= CREW_RULES.chestMinHours * 3_600_000 && m.levelXp > 0;
}

// ---- Crew XP -------------------------------------------------------------------------------------------------------

export type CrewXpSource = 'contract' | 'horde' | 'runs' | 'donate';

export const CREW_XP = {
  /** Runs: per energy a member spends sending a run (target ~25% of an active crew's XP). */
  perEnergy: 2,
  /** The horde: per wave a member fights (live or on guard), and per fighter when the horde falls (~25%). */
  perWave: 6,
  hordeWin: 10,
  /** Donations (~15%): one crew XP for this much value (`STACKS.value`), at most `donateDaily` a member a day. */
  donateValuePerXp: 10,
  donateDaily: 15,
};

/** What can be donated and sent: the ten basic resources (Food, Ammo and Fuel too, as a way to use up a surplus). */
export const CREW_RESOURCES: StackKind[] = ['food', 'wood', 'cloth', 'metal', 'fuel', 'electronics', 'chemicals', 'medicine', 'ammo', 'scrap'];

/** Value of stacks for crew rules (`STACKS.value`, no rounding). */
export function crewValue(stacks: StackMap): number {
  let v = 0;
  for (const [k, q] of Object.entries(stacks) as [StackKind, number][]) v += (STACKS[k]?.value ?? 0) * (q ?? 0);
  return v;
}

/**
 * A donation: the crew XP it gives with `usedToday` XP already given today, and how much of it is taken (a donation
 * beyond the day's limit is refused, so nothing is lost for nothing).
 */
export function donationXp(stacks: StackMap, usedToday: number): { xp: number; value: number; left: number } {
  const value = crewValue(stacks);
  const left = Math.max(0, CREW_XP.donateDaily - usedToday);
  return { xp: Math.min(left, Math.floor(value / CREW_XP.donateValuePerXp)), value, left };
}

// ---- The weekly crew contract ----------------------------------------------------------------------------------------

export type CrewGoalId = 'energy' | 'waves' | 'contracts';

export const CREW_CONTRACT = {
  /** From crew level 2. */
  fromLevel: 2,
  /** Goals scale with the members when the week's contract is made (at least 2). */
  minMembers: 2,
  goals: [
    { id: 'energy' as CrewGoalId, perMember: 45, name: 'Out there', text: 'Spend {n} energy on runs' },
    // 23. 10. 2026 (owner): the hordes a member took part in, once a horde (live, on guard, or on guard when the horde fell
    // before their wave), not the waves: a horde that falls in its first minute made waves a reason to hold back. The id
    // stays `waves` (the stored progress).
    { id: 'waves' as CrewGoalId, perMember: 4, name: 'Hold the line', text: 'Join {n} hordes' },
    { id: 'contracts' as CrewGoalId, perMember: 9, name: 'Every job done', text: 'Claim {n} daily contracts' },
  ],
  /** Crew XP a goal gives × members, and all three on top (~35% of an active crew's XP). */
  xpPerGoal: 70,
  xpAll: 35,
};

export interface CrewContractState {
  /** Monday of the week (YYYY-MM-DD). */
  week: string;
  members: number;
  progress: Record<CrewGoalId, number>;
  done: CrewGoalId[];
  all: boolean;
  /** 23. 10. 2026: what counted once only this week (`<horde>:<user>` for Hold the line). */
  seen?: string[];
}

export const crewGoalTarget = (id: CrewGoalId, members: number) => (CREW_CONTRACT.goals.find((g) => g.id === id)?.perMember ?? 0) * Math.max(CREW_CONTRACT.minMembers, members);

export function newCrewContract(week: string, members: number): CrewContractState {
  return { week, members: Math.max(CREW_CONTRACT.minMembers, Math.min(CREW_RULES.maxMembers, members)), progress: { energy: 0, waves: 0, contracts: 0 }, done: [], all: false };
}

/**
 * Adds progress to a goal: the goals finished now and the crew XP they give. `once` (23. 10. 2026): a key that counts only
 * once this week (a member's horde), however often it comes.
 */
export function crewContractAdd(c: CrewContractState, goal: CrewGoalId, n: number, once?: string): { c: CrewContractState; finished: CrewGoalId[]; all: boolean; xp: number } {
  if (n <= 0 || c.done.includes(goal) || (once && c.seen?.includes(once))) return { c, finished: [], all: false, xp: 0 };
  if (once) c = { ...c, seen: [...(c.seen ?? []), once] };
  const progress = { ...c.progress, [goal]: Math.min(crewGoalTarget(goal, c.members), (c.progress[goal] ?? 0) + n) };
  const finished = progress[goal] >= crewGoalTarget(goal, c.members) ? [goal] : [];
  const done = [...c.done, ...finished];
  const all = !c.all && done.length === CREW_CONTRACT.goals.length;
  const xp = finished.length * CREW_CONTRACT.xpPerGoal * c.members + (all ? CREW_CONTRACT.xpAll * c.members : 0);
  return { c: { ...c, progress, done, all: c.all || all }, finished, all, xp };
}

// ---- The horde -------------------------------------------------------------------------------------------------------

export const CREW_HORDE = {
  /** The damage bonus by crew level, when at least `minFighters` members fight the same evening. */
  bonus: [
    { level: 3, pct: 0.05 },
    { level: 8, pct: 0.1 },
  ],
  minFighters: 2,
  /**
   * The shared chest (days of materials for the crew, split equally among the members who fought): `perWave` days for each
   * wave's worth of the crew's damage (damage ÷ one wave at the fighters' level), × `win` or `loss`, × `bigFrom` level's
   * `big`; at most `maxPerFighter` days a fighter.
   */
  chest: { perWave: 0.12, win: 1.25, loss: 0.75, bigFrom: 8, big: 1.5, maxPerFighter: 0.6 },
};

export function crewHordeBonus(level: number, fighters: number): number {
  if (fighters < CREW_HORDE.minFighters) return 0;
  let pct = 0;
  for (const b of CREW_HORDE.bonus) if (level >= b.level) pct = b.pct;
  return pct;
}

/** The shared chest's days of materials for the whole crew (0 with fewer than two fighters). */
export function crewHordeChestDays(o: { damage: number; waveDamage: number; fighters: number; victory: boolean; level: number }): number {
  if (o.fighters < CREW_HORDE.minFighters || o.damage <= 0) return 0;
  const c = CREW_HORDE.chest;
  const days = c.perWave * (o.damage / Math.max(1, o.waveDamage)) * (o.victory ? c.win : c.loss) * (o.level >= c.bigFrom ? c.big : 1);
  return Math.round(Math.min(days, c.maxPerFighter * o.fighters) * 100) / 100;
}

/** The rally line ("Rally at Route 9 tonight: Tollmen raid."). */
export const crewRallyText = (zone: string, what: string) => `Rally at ${zone} tonight: ${what}.`;

// ---- Sending ---------------------------------------------------------------------------------------------------------

export const CREW_SEND = {
  fromLevel: 4,
  /** The value (`STACKS.value`) a member may send a day, and receive: `base` at level 4, + `perLevel` a level. */
  base: 150,
  perLevel: 50,
};

export function crewSendCap(level: number): number {
  return level < CREW_SEND.fromLevel ? 0 : CREW_SEND.base + CREW_SEND.perLevel * (level - CREW_SEND.fromLevel);
}

// ---- Crew Rank -------------------------------------------------------------------------------------------------------

/** Crew Rank: the crew XP of every season together (cosmetic: the flag's frame). */
export const CREW_RANK = { stepXp: 10_000 };
export const crewRankOf = (totalXp: number) => Math.floor(Math.max(0, totalXp) / CREW_RANK.stepXp);
/** The flag's frame by rank: none, thin, double, full. */
export const crewFrameOf = (rank: number): 0 | 1 | 2 | 3 => (rank >= 5 ? 3 : rank >= 3 ? 2 : rank >= 1 ? 1 : 0);

// ---- Chat ------------------------------------------------------------------------------------------------------------

export const CREW_CHAT = {
  maxLength: 300,
  /** At most `burst` messages in `burstSeconds`; the same text not twice in `duplicateSeconds`. */
  burst: 5,
  burstSeconds: 30,
  duplicateSeconds: 30,
  /** Messages are kept this long; the chat shows the last `shown`. */
  historyDays: 30,
  shown: 100,
  /** 23. 10. 2026 (owner): the camp radio's lines left the chat; the crew's Overview lists the latest `radioShown`. */
  radioShown: 10,
  /** 23. 10. 2026 (owner): unread crewmates' messages are counted up to this (the Chat tab shows `9+` above 9). */
  unreadMax: 99,
};

/** Whether a member may send now: the times of their last messages (ms) and the text of the last one. */
export function chatRateOk(recent: { at: number; body: string }[], body: string, now: number): 'ok' | 'too_fast' | 'duplicate' {
  const inBurst = recent.filter((m) => now - m.at < CREW_CHAT.burstSeconds * 1000);
  if (inBurst.length >= CREW_CHAT.burst) return 'too_fast';
  const norm = (s: string) => s.trim().toLowerCase();
  if (recent.some((m) => now - m.at < CREW_CHAT.duplicateSeconds * 1000 && norm(m.body) === norm(body))) return 'duplicate';
  return 'ok';
}

// ---- Membership helpers ----------------------------------------------------------------------------------------------

/** Whether this survivor may join a crew: level 8, or the account's first week over. */
export function crewJoinOpen(level: number, accountDays: number): boolean {
  return level >= CREW_RULES.joinLevel || accountDays >= CREW_RULES.joinAfterDays;
}

/**
 * The same IP (owner: "isti IP ne donosi XP ni bodove crewu"): of members on one IP only the one who joined first brings
 * crew XP and points; the others are shadowed. Returns the shadowed user ids.
 */
export function ipShadowed(members: { userId: string; joinedAt: number; ip: string | null }[]): Set<string> {
  const out = new Set<string>();
  const first = new Map<string, string>();
  for (const m of [...members].sort((a, b) => a.joinedAt - b.joinedAt || a.userId.localeCompare(b.userId))) {
    if (!m.ip) continue;
    if (first.has(m.ip)) out.add(m.userId);
    else first.set(m.ip, m.userId);
  }
  return out;
}

/** Who takes over from a leader away too long: the officer seen last (then the most XP), else such a member. */
export function crewHeir(members: { userId: string; role: string; lastSeenAt: number | null; xp: number }[], leaderId: string): string | null {
  const rest = members.filter((m) => m.userId !== leaderId);
  const best = (list: typeof rest) => [...list].sort((a, b) => (b.lastSeenAt ?? 0) - (a.lastSeenAt ?? 0) || b.xp - a.xp || a.userId.localeCompare(b.userId))[0] ?? null;
  return (best(rest.filter((m) => m.role === 'officer')) ?? best(rest))?.userId ?? null;
}

/** The order of the open crews offered to a player without one: level closest to theirs, then the most active. */
export function crewSuggestOrder(a: { avgLevel: number; active: number; members: number }, b: { avgLevel: number; active: number; members: number }, myLevel: number): number {
  return Math.abs(a.avgLevel - myLevel) - Math.abs(b.avgLevel - myLevel) || b.active - a.active || b.members - a.members;
}

/** A tag: 3–4 letters or digits, upper case. */
export const CREW_TAG_RE = /^[A-Z0-9]{3,4}$/;

// ---- The flag (the emblem editor) ------------------------------------------------------------------------------------

/**
 * Flag parts a crew level opens (level 5: two colours; level 9: two shapes and two symbols). 11. 10. 2026 (owner): a
 * season's reward is a frame and a title only (no Gold colour); the symbols are the six drawn flags, the sun over the road
 * and the wolf at level 9.
 */
export const CREW_FLAG_UNLOCKS = {
  colors: { 5: ['#3E8E86', '#8A5A9E'] } as Record<number, string[]>,
  shapes: { 9: ['hex', 'pennant'] } as Record<number, string[]>,
  symbols: { 9: ['road', 'wolf'] } as Record<number, string[]>,
};

function unlockedAt<T>(table: Record<number, T[]>, level: number): T[] {
  return Object.entries(table).flatMap(([lv, list]) => (level >= Number(lv) ? list : []));
}

/** Whether a flag part is open for a crew of this level. */
export function crewFlagPartOpen(kind: 'color' | 'shape' | 'symbol', value: string, level: number, base: string[]): boolean {
  if (base.includes(value)) return true;
  if (kind === 'color') return unlockedAt(CREW_FLAG_UNLOCKS.colors, level).includes(value);
  if (kind === 'shape') return unlockedAt(CREW_FLAG_UNLOCKS.shapes, level).includes(value);
  return unlockedAt(CREW_FLAG_UNLOCKS.symbols, level).includes(value);
}

// ---- Season honors -------------------------------------------------------------------------------------------------

export interface CrewHonor {
  season: number;
  kind: 'score' | 'horde';
  place: number;
  title: string;
  /** A Crew Season Score honor also puts a season frame on the flag. */
  frame?: boolean;
}

/**
 * 11. 10. 2026 (owner): the season's rewards for crews, cosmetic only. The top 3 crews by Crew Season Score get a flag frame
 * and a title; with more than 30 crews on the list (Crew Season Score above 0), the top 10% instead. The crew with the most
 * horde damage in the season gets a title.
 */
export const CREW_SEASON_HONORS = {
  top: 3,
  manyFrom: 30,
  topShare: 0.1,
  scorePlaces: ['First Crew', 'Second Crew', 'Third Crew'],
  /** Past the third place (only with more than 30 crews). */
  scoreTop: 'Top Crew',
  horde: 'Horde Wall',
};

/** How many crews of the Crew Season Score list get the season's frame and title. */
export function crewHonorPlaces(crews: number): number {
  const h = CREW_SEASON_HONORS;
  return crews > h.manyFrom ? Math.ceil(crews * h.topShare) : Math.min(h.top, crews);
}

/** The season title of a Crew Season Score place ("First Crew, Season 2"; past the third "Top Crew, Season 2"); the name players see (`seasonName`). */
export const crewScoreTitle = (place: number, season: number) => `${CREW_SEASON_HONORS.scorePlaces[place - 1] ?? CREW_SEASON_HONORS.scoreTop}, ${seasonName(season)}`;

/** The season title of the crew with the most horde damage. */
export const crewHordeTitle = (season: number) => `${CREW_SEASON_HONORS.horde}, ${seasonName(season)}`;

/** The flag's season frame: any Crew Season Score honor (kept for good, like Crew Rank). */
export const crewSeasonFrame = (honors: CrewHonor[]) => honors.some((h) => h.kind === 'score' && h.frame);

// ---- The crew outfit (crew level 10) ---------------------------------------------------------------------------------

/**
 * 11. 10. 2026 (owner): crew level 10's outfit is, for now, an icon in the Collection and the title The Last Five (not on
 * the survivor yet: BACKLOG). The member who opens the level 10 chest owns it for good.
 */
export const CREW_OUTFIT = { name: 'Crew outfit', title: 'The Last Five', art: 'cosmetic_crew_outfit' };
